// src/components/discovery/ProblemCardWindow.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Loader2, Save, XCircle } from "lucide-react";
import { problemCardsService, okrsService } from "../../services/database";
import ConfidencePicker, { CONFIDENCE_DIMENSIONS } from "./ConfidencePicker";

async function robustSaveProblem(problemId, patch) {
  try {
    console.log('Updating problem card:', problemId, 'with:', patch);
    return await problemCardsService.update(problemId, patch);
  } catch (error) {
    console.error('Error updating problem card:', error);
    throw error;
  }
}

const SEVERITIES = ["low", "medium", "high", "critical"];
const STATUSES = ["Discovery", "validated", "solution_designed", "in_roadmap", "solved"];

export default function ProblemCardWindow({
  open,
  problem,
  onClose,
  onSaved,        // called after successful save
}) {
  const safe = problem || {};
  const [busy, setBusy] = useState(false);
  const [okrs, setOkrs] = useState([]);

  // Local form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [customerSegment, setCustomerSegment] = useState("");
  const [severity, setSeverity] = useState("low");
  const [status, setStatus] = useState("Discovery");
  const [okr, setOkr] = useState("");
  const [horizon, setHorizon] = useState("");
  const [desirability, setDesirability] = useState(0);
  const [feasibility, setFeasibility] = useState(0);
  const [viability, setViability] = useState(0);
  const scores = {
    desirability: { value: desirability, set: setDesirability },
    feasibility: { value: feasibility, set: setFeasibility },
    viability: { value: viability, set: setViability },
  };

  // Load OKRs when dialog opens
  useEffect(() => {
    if (!open) return;

    const loadOkrs = async () => {
      try {
        const okrData = await okrsService.list("-updated_at", 100);
        setOkrs(okrData);
      } catch (error) {
        console.error("Failed to load OKRs:", error);
        setOkrs([]);
      }
    };

    loadOkrs();
  }, [open]);

  // hydrate when opening / when problem changes
  useEffect(() => {
    if (!open) return;
    setTitle(safe.title || "");
    setDescription(safe.description || safe.problem || "");
    setCustomerSegment(safe.target_segment || safe.customer_segment || "");
    setSeverity((safe.severity && String(safe.severity).toLowerCase()) || "low");
    setStatus((safe.status && String(safe.status)) || "Discovery");
    setOkr(safe.okr_ref || safe.okr || "");
    setHorizon(safe.time_horizon || safe.horizon || "");

    // Handle JSONB confidence_score or legacy individual fields
    const confidence = safe.confidence_score || {};
    setDesirability(Number(confidence.desirability || safe.desirability_score) || 0);
    setFeasibility(Number(confidence.feasibility || safe.feasibility_score) || 0);
    setViability(Number(confidence.viability || safe.viability_score) || 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, safe.id]);

  const canSave = useMemo(() => {
    return String(title).trim().length > 0;
  }, [title]);

  const handleSave = async () => {
    if (!canSave || !safe?.id) return;
    setBusy(true);
    try {
      const patch = {
        title: title?.trim(),
        description: description?.trim(),
        target_segment: customerSegment?.trim(),
        severity,
        status,
        okr_ref: okr?.trim(),
        time_horizon: horizon?.trim(),
        confidence_score: {
          desirability: Number(desirability) || 0,
          feasibility: Number(feasibility) || 0,
          viability: Number(viability) || 0
        },
        updated_at: new Date().toISOString(),
      };
      await robustSaveProblem(safe.id, patch);
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error("[ProblemCardWindow] Save failed:", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        style={{ maxWidth: 900, width: "100%", }}
      >
        <DialogHeader>
          <DialogTitle style={{ display: "flex", alignItems: "center", gap: 12 }}>
            Problem Card{safe?.id ? ` #${safe.id}` : ""}
          </DialogTitle>
          <DialogDescription>
            View or edit the problem’s core details. All changes are saved to the Problem Card entity.
          </DialogDescription>
        </DialogHeader>

        <div style={{ display: "grid", gap: 16 }}>
          {/* Title */}
          <div>
            <Label htmlFor="pcw-title">Title</Label>
            <Input
              id="pcw-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Short, user-centered problem statement"
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="pcw-desc">Description</Label>
            <Textarea
              id="pcw-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Context, user quotes, pain symptoms, impact, scope assumptions…"
            />
          </div>

          {/* Row: Customer segment + OKR */}
          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" }}>
            <div>
              <Label htmlFor="pcw-seg">Customer segment</Label>
              <Input
                id="pcw-seg"
                value={customerSegment}
                onChange={(e) => setCustomerSegment(e.target.value)}
                placeholder="e.g. SMB sellers in Israel"
              />
            </div>

            <div>
              <Label>Linked OKR (optional)</Label>
              <Select value={okr} onValueChange={setOkr}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an OKR..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {okrs.map((okrItem) => (
                    <SelectItem key={okrItem.id} value={String(okrItem.id)}>
                      {okrItem.title} ({okrItem.quarter} {okrItem.year})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row: Horizon + Severity + Status */}
          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr 1fr" }}>
            <div>
              <Label htmlFor="pcw-hzn">Time horizon</Label>
              <Input
                id="pcw-hzn"
                value={horizon}
                onChange={(e) => setHorizon(e.target.value)}
                placeholder="Now / Next / Later (or Q3 2025)"
              />
            </div>

            <div>
              <Label>Severity</Label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger><SelectValue placeholder="Choose severity" /></SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue placeholder="Choose status" /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Confidence */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Label>Confidence</Label>
              <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>
                Average: {Math.round(((Number(desirability)||0)+(Number(feasibility)||0)+(Number(viability)||0))/3)}%
              </span>
            </div>
            <p style={{ margin: "2px 0 8px", fontSize: 12, color: "var(--fg-muted)" }}>
              How strong is the evidence today?
            </p>

            <div
              style={{
                display: "grid",
                gap: 14,
                background: "var(--bg-subtle)",
                padding: 12,
                borderRadius: 12,
              }}
            >
              {CONFIDENCE_DIMENSIONS.map((dim) => (
                <ConfidencePicker
                  key={dim.id}
                  dimension={dim}
                  value={scores[dim.id].value}
                  onChange={scores[dim.id].set}
                />
              ))}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 8, borderTop: "1px solid var(--border)" }}>
            <Button type="button" variant="outline" onClick={() => onClose?.()} disabled={busy}>
              <XCircle size={14} style={{ marginRight: 6 }} />
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!canSave || busy}
              style={{ background: "linear-gradient(90deg,var(--success),var(--success))", border: "none", color: "#fff" }}
              title={!canSave ? "Please provide a title" : "Save changes"}
            >
              {busy ? (
                <>
                  <Loader2 size={14} className="animate-spin" style={{ marginRight: 6 }} />
                  Saving…
                </>
              ) : (
                <>
                  <Save size={14} style={{ marginRight: 6 }} />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
